const { Set } = require('immutable');

/**
 * 階層構造のデータを再帰処理用にchildrenプロパティに変換する関数
 * 
 * @param {Array} data - 変換対象の配列データ
 * @param {Array<string>} childrenPropertyNames - childrenに変換するプロパティ名の配列
 * @returns {Array} - 変換後の配列（childrenプロパティを持つ構造）
 *
 * @example
 * 基本的な使用例
 * const data = [
 *   { id: 1, name: 'A', subItems: [...] }
 * ];
 * const transformed = transformToChildrenStructure(data, ['subItems']);
 * 結果: [{ id: 1, name: 'A', children: [...] }]
 *
 * @example
 * 業界カテゴリの階層構造変換例
 * const industryData = [
 *   {
 *     id: 1,
 *     code: "A",
 *     name: "農業，林業",
 *     industry_middle_categories: [
 *       {
 *         id: 1,
 *         name: "農業",
 *         industry_minor_categories: [
 *           { id: 1, name: "管理，補助的経済活動を行う事業所（01農業）" },
 *           { id: 2, name: "耕種農業" }
 *         ]
 *       }
 *     ]
 *   }
 * ];
 * const transformed = transformToChildrenStructure(industryData, ['industry_middle_categories', 'industry_minor_categories']);
 * 結果: [
 *   {
 *     id: 1,
 *     code: "A",
 *     name: "農業，林業",
 *     children: [
 *       {
 *         id: 1,
 *         name: "農業",
 *         children: [
 *           { id: 1, name: "管理，補助的経済活動を行う事業所（01農業）" },
 *           { id: 2, name: "耕種農業" }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 */
export const transformToChildrenStructure = (data, childrenPropertyNames = []) => {
  if (!Array.isArray(data)) {
    return data;
  }
  if (!Array.isArray(childrenPropertyNames) || childrenPropertyNames.length === 0) {
    return data;
  }

  return data.map(item => {
    const transformedItem = { ...item };

    // 指定されたプロパティ名をchildrenに変換
    childrenPropertyNames.forEach(propName => {
      if (item && item[propName]) {
        transformedItem.children = transformToChildrenStructure(item[propName], childrenPropertyNames);
        delete transformedItem[propName];
      }
    });

    return transformedItem;
  });
};

/**
 * 判定: ノードが葉かどうか
 *
 * @param {Object} node - メニューのノードオブジェクト
 * @param {number|string} node.id - ノードのID
 * @param {Object[]} [node.children] - 子ノード配列
 * @returns {boolean} - true: 子を持たない or null/undefined, false: 子が存在
 */
export const isLeaf = (node) =>
  !node || !node.children || node.children.length === 0;

/**
 * ツリー全体から「葉ノードのID」一覧を抽出する
 *
 * @param {Object[]} menuItems - メニューのノード配列
 * @returns {number[]} - 葉ノードIDの配列（number化済み）
 */
export const flattenLeafIdsFromTree = (menuItems) => {
  if (!Array.isArray(menuItems)) {
    return [];
  }
  const ids = [];
  const stack = [...menuItems];
  while (stack.length) {
    const n = stack.pop();
    if (!n) {
      continue;
    }
    if (n && isLeaf(n)) {
      if (n.id !== undefined && n.id !== null) {
        ids.push(Number(n.id));
      }
    } else if (n.children && n.children.length) {
      stack.push(...n.children);
    }
  }
  return ids;
};

/**
 * 指定ノード以下の「葉ノードのID」一覧を取得する
 *
 * @param {Object} node - 基準ノード
 * @returns {number[]} - 葉ノードIDの配列
 */
export const getAllLeafIds = (node) => {
  if (!node || node.id === undefined || node.id === null) {
    return [];
  }
  if (isLeaf(node)) {
    return [Number(node.id)];
  }
  const ids = [];
  const stack = [...(node.children || [])];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur) {
      continue;
    }
    if (cur && isLeaf(cur)) {
      if (cur.id !== undefined && cur.id !== null) {
        ids.push(Number(cur.id));
      }
    } else if (cur.children && cur.children.length) {
      stack.push(...cur.children);
    }
  }
  return ids;
};

/**
 * 判定: 直下の子がすべて葉かどうか
 *
 * @param {Object[]} children - 子ノード配列
 * @returns {boolean} - true: 全ての子が葉, false: 内部ノードが含まれる
 */
export const areAllChildrenLeaf = (children) =>
  Array.isArray(children) && children.every((c) => isLeaf(c));

/**
 * ノードの選択状態（チェック済み/未チェック/中間）を返す
 *
 * @param {Object} node - ノード
 * @param {Set<number>} selectedLeafSet - 選択済み葉IDの Immutable Set
 * @returns {{checked: boolean, indeterminate: boolean}} - 視覚的状態
 */
export const getSelectionVisualState = (node, selectedLeafSet) => {
  const leafIds = getAllLeafIds(node);
  const total = leafIds.length;
  if (total === 0) {
    return { checked: false, indeterminate: false };
  }
  const selected = leafIds.filter((id) => selectedLeafSet.has(id)).length;
  return {
    checked: selected === total,
    indeterminate: selected > 0 && selected < total,
  };
};

/**
 * 入力の Set をツリー上の「葉ID」に制限して正規化する
 *
 * @param {Set|Object} maybeSet - 既存の選択状態（Immutable Set or Map 等）
 * @param {Object[]} tree - ツリー構造の配列
 * @returns {Set<number>} - ツリー上に存在する葉IDのみを含む Immutable Set
 */
export const sanitizeToLeafSet = (maybeSet, tree) => {
  let s = maybeSet || Set();
  if (s && typeof s.toSet === 'function') {
    s = s.toSet();
  }
  else if (s && typeof s.toArray === 'function') {
    s = Set(s.toArray());
  }
  else if (!Set.isSet(s)) {
    s = Set();
  }

  const leafIdSet = new Set(flattenLeafIdsFromTree(tree));
  return s.filter((id) => leafIdSet.has(Number(id)));
};

/**
 * 選択済みの葉ID集合から、できるだけ上位ノードで代表させた最小集合を返す。
 * - あるノード配下の全葉が選択されていれば、そのノード1つだけを返す（子は返さない）
 * - 一部のみ選択なら、子側の結果を再帰的に結合
 * - 葉が単体で選択されていれば、その葉ノードを返す
 *
 * @param {Object[]} hierarchicalMenu - ルート配列（複数ルート可）
 * @param {Set<number>} selectedLeafSet - 選択済みの葉ID（Immutable Setを想定、number化される）
 * @returns {Object[]} - 代表ノードの配列（入力ツリー中のノードオブジェクトをそのまま返す）
 */
export const compressSelectionToNodes = (hierarchicalMenu, selectedLeafSet) => {
  const safeSelected =
    selectedLeafSet && typeof selectedLeafSet.has === 'function'
      ? selectedLeafSet
      : new Set(); // Immutable Set 以外でも has を持てばOKにする

  const dfs = (node) => {
    if (!node) {
      return { leafCount: 0, selectedLeafCount: 0, nodes: [] };
    }

    // 葉
    if (isLeaf(node)) {
      const leafCount = 1;
      const selectedLeafCount = safeSelected.has(Number(node.id)) ? 1 : 0;
      return {
        leafCount,
        selectedLeafCount,
        nodes: selectedLeafCount === 1 ? [node] : [],
      };
    }

    // 内部ノード
    let leafCount = 0;
    let selectedLeafCount = 0;
    let acc = [];

    const children = Array.isArray(node.children) ? node.children : [];
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      const res = dfs(child);
      leafCount += res.leafCount;
      selectedLeafCount += res.selectedLeafCount;
      if (res && res.nodes && res.nodes.length) {
        acc = acc.concat(res.nodes);
      }
    }

    // 配下の全葉が選択済みなら、このノード一発で代表（子の代表は捨てる）
    if (leafCount > 0 && selectedLeafCount === leafCount) {
      return { leafCount, selectedLeafCount, nodes: [node] };
    }

    // 部分選択 or 未選択 → 子側の代表をそのまま返す
    return { leafCount, selectedLeafCount, nodes: acc };
  };

  const result = [];
  const roots = Array.isArray(hierarchicalMenu) ? hierarchicalMenu : [];
  for (let i = 0; i < roots.length; i++) {
    const r = roots[i];
    const { nodes } = dfs(r);
    if (nodes && nodes.length) result.push(...nodes);
  }

  return result;
};

/**
 * 選択済みの葉ID集合を、可能なかぎり上位ノードで代表させた「最小集合」に圧縮し、
 * 各代表ノードへのルートからのパス（ノード配列）を返す。
 *
 * 振る舞い:
 * - あるノード配下の全ての葉が選択されている → そのノード1つで代表（子は返さない）
 * - 一部のみ選択されている → 代表は子側に降りる（葉が選択ならその葉）
 * - 代表が複数ある場合 → それぞれのパスを配列要素として返す
 *
 * @param {Object[]} hierarchicalMenu - ルート配列（複数ルート可）
 * @param {Set<number>} selectedLeafSet - 選択済みの葉ID集合（`has(id)` を持つSet。数値比較）
 * @returns {Object[][]} - 各代表ノードまでのパス配列（各パスはノード配列）
 *
 * @example
 * ツリー例:
 * A(1)
 *  ├─ B(2)
 *  │   ├─ D(4)
 *  │   └─ E(5)
 *  └─ C(3)
 *      └─ F(6)
 * const tree = [
 *   { id: 1, name: 'A', children: [
 *     { id: 2, name: 'B', children: [
 *       { id: 4, name: 'D' },
 *       { id: 5, name: 'E' },
 *     ]},
 *     { id: 3, name: 'C', children: [
 *       { id: 6, name: 'F' },
 *     ]},
 *   ]},
 * ];
 *
 * 1) selectedLeafSet = {4,5}
 *    → B配下の葉がすべて選択 → 代表はB → パス [A,B]
 *    compressSelectionToPaths(tree, new Set([4,5]))
 *    => [[{id:1,'A'}, {id:2,'B'}]]
 *
 * 2) selectedLeafSet = {4}
 *    → Dのみ選択 → 代表はD（葉） → パス [A,B,D]
 *    compressSelectionToPaths(tree, new Set([4]))
 *    => [[{id:1,'A'}, {id:2,'B'}, {id:4,'D'}]]
 *
 * 3) selectedLeafSet = {4,5,6}
 *    → A配下の葉が全選択 → 代表はA → パス [A]
 *    compressSelectionToPaths(tree, new Set([4,5,6]))
 *    => [[{id:1,'A'}]]
 *
 * 4) selectedLeafSet = {4,6}
 *    → B配下は一部（Dのみ） → 代表はD → パス [A,B,D]
 *    → C配下はFだけで全葉選択 → 代表はC → パス [A,C]
 *    compressSelectionToPaths(tree, new Set([4,6]))
 *    => [[{id:1,'A'}, {id:2,'B'}, {id:4,'D'}], [{id:1,'A'}, {id:3,'C'}]]
 *
 * 5) 複数ルート構成の例（7の親は1ではなく、1と同階層の別ルート）
 *    ルート: [ A(1), X(10) ] かつ X(10) 配下に G(7)
 *    selectedLeafSet = {4,7}
 *    → A側は D が代表 → パス [A,B,D]
 *    → X側は G が代表（単体葉） → パス [X,G]
 *    compressSelectionToPaths([
 *      tree[0],
 *      { id: 10, name: 'X', children: [{ id: 7, name: 'G' }] }
 *    ], new Set([4,7]))
 *    => [[{id:1,'A'}, {id:2,'B'}, {id:4,'D'}], [{id:10,'X'}, {id:7,'G'}]]
 *
 * 別ツリー例（AがTOPでない複数ルート構成）:
 * P(100)
 *  ├─ Q(110)
 *  │   ├─ R(111)
 *  │   └─ S(112)
 *  └─ T(120)
 * U(200)
 *  └─ V(210)
 *      ├─ W(211)
 *      └─ Z(212)
 * const treeMulti = [
 *   { id: 100, name: 'P', children: [
 *     { id: 110, name: 'Q', children: [
 *       { id: 111, name: 'R' },
 *       { id: 112, name: 'S' },
 *     ]},
 *     { id: 120, name: 'T' },
 *   ]},
 *   { id: 200, name: 'U', children: [
 *     { id: 210, name: 'V', children: [
 *       { id: 211, name: 'W' },
 *       { id: 212, name: 'Z' },
 *     ]},
 *   ]},
 * ];
 *
 * 6) selectedLeafSet = {111,112}
 *    → Q配下の葉が全選択 → 代表はQ → パス [P,Q]
 *    compressSelectionToPaths(treeMulti, new Set([111,112]))
 *    => [[{id:100,'P'}, {id:110,'Q'}]]
 *
 * 7) selectedLeafSet = {112,211}
 *    → Q配下は一部選択 → 代表はS → パス [P,Q,S]
 *    → V配下は一部選択 → 代表はW → パス [U,V,W]
 *    compressSelectionToPaths(treeMulti, new Set([112,211]))
 *    => [[{id:100,'P'}, {id:110,'Q'}, {id:112,'S'}], [{id:200,'U'}, {id:210,'V'}, {id:211,'W'}]]
 */
export const compressSelectionToPaths = (hierarchicalMenu, selectedLeafSet) => {
  const safeSelected =
    selectedLeafSet && typeof selectedLeafSet.has === 'function'
      ? selectedLeafSet
      : new Set();

  const dfs = (node, stack) => {
    if (!node) {
      return { leafCount: 0, selectedLeafCount: 0, paths: [] };
    }

    const currentStack = Array.isArray(stack) ? stack : [];
    const nextStack = currentStack.concat(node);

    // 葉
    if (isLeaf(node)) {
      const leafCount = 1;
      const selectedLeafCount = safeSelected.has(Number(node.id)) ? 1 : 0;
      return {
        leafCount,
        selectedLeafCount,
        paths: selectedLeafCount === 1 ? [nextStack] : [],
      };
    }

    // 内部ノード
    let leafCount = 0;
    let selectedLeafCount = 0;
    let accPaths = [];

    const children = Array.isArray(node.children) ? node.children : [];
    for (let i = 0; i < children.length; i++) {
      const res = dfs(children[i], nextStack);
      leafCount += res.leafCount;
      selectedLeafCount += res.selectedLeafCount;
      if (res && res.paths && res.paths.length) {
        accPaths = accPaths.concat(res.paths);
      }
    }

    // 配下の全葉が選択済みなら、このノード一発で代表（子の代表は捨てる）
    if (leafCount > 0 && selectedLeafCount === leafCount) {
      return { leafCount, selectedLeafCount, paths: [nextStack] };
    }

    // 部分選択 or 未選択 → 子側の代表パスをそのまま返す
    return { leafCount, selectedLeafCount, paths: accPaths };
  };

  const result = [];
  const roots = Array.isArray(hierarchicalMenu) ? hierarchicalMenu : [];
  for (let i = 0; i < roots.length; i++) {
    const { paths } = dfs(roots[i], []);
    if (paths && paths.length) result.push(...paths);
  }

  return result;
};



// WEBPACK FOOTER //
// ./src/utils/treeUtils.js